/**
 * A3 · ফোন নম্বর — `/auth/phone`
 * Frame: `Oprappo P0 Screens.dc.html`, "A3 · ফোন নম্বর".
 *
 * Frame note: "Terms as a sentence above the button, not a checkbox — a tick box next
 * to legal text is consent theatre."
 *
 * CORRECTION (defect 1, Bangla digits in Manrope): the country-code box is drawn
 * `500 17px/1 Manrope,sans-serif` carrying `+৮৮০` — Bangla digits in a family that has
 * none. Set in Anek Bangla at the same 17px, leading 1.75 per the measured band
 * (docs/02 § line height). The box is `h-[58px]` and flex-centred, so the glyph does
 * not move. Listed in the WP-C report.
 *
 * ── W2-12 · A CLEAN SCREEN THAT ANSWERS ──────────────────────────────────────
 *
 * Recorded as a departure from the frame in `docs/deviations/W2-12.md`:
 *
 *   · The field starts EMPTY, focused, with a format placeholder. The frame draws it
 *     already carrying a sample number, and a real person arriving here was handed a
 *     number that was not theirs — "nothing dummy left" (founder, 2026-09-18). The
 *     placeholder is a shape, `1XXX XXX XXX`, not a number anybody owns.
 *   · The channel is a CHOICE — docs/07 § 3's "offer it as a choice, not only as a
 *     failure path" — read from `GET /v1/auth/channels`, with Telegram's extra step
 *     (`door.tsx § the Telegram step`).
 *   · Failures answer: the field's own error for a number that cannot be a mobile, a
 *     line above the button for a throttle, a closed Telegram door or a refusal, and a
 *     frame when the person's situation changed (`auth/failure.tsx`).
 *   · With a Turnstile sitekey bound, the button waits for a token (`turnstile.ts`).
 *   · শর্তাবলি and গোপনীয়তা নীতি are links to the documents they name.
 *
 * The heading, the sentence under it, the label, the `+৮৮০` box, the hint, the terms
 * sentence and the button are as drawn.
 */
import { Link, useNavigate } from 'react-router-dom';
import { Button, IconButton } from '@oprappo/ui';
import { ChannelChoice, DoorNoticeLine, TelegramPanel, useCodeDoor } from './door.js';
import { FIELD_ERROR_BOX, FIELD_ERROR_LINE, FIELD_LABEL, Frame, SAND_FRAME } from './frame.js';

const WHERE = 'A3 · POST /v1/auth/otp/request';

/** The format the field expects, as a shape — never a number somebody owns. */
export const A3_PLACEHOLDER = '1XXX XXX XXX';

export function PhoneScreen(): JSX.Element {
  const navigate = useNavigate();
  const door = useCodeDoor({ intent: 'signup', next: null, where: WHERE });

  if (door.frame !== null) return door.frame;

  return (
    <Frame className={`${SAND_FRAME} px-[24px] pt-[20px] pb-[24px] overflow-y-auto`}>
      {/*
        `flex` is load-bearing, not decoration. `IconButton` renders an `inline-flex`
        button, so in a plain block wrapper it sits in a line box and picks up the
        inherited 16px font's descender space — the row measures 32px against the
        drawn 22px svg and every element below it lands 10px low.
      */}
      <div className="flex">
        <IconButton control="back" size={22} onClick={() => navigate(-1)} />
      </div>

      <div className="mt-[30px]">
        <h1 className="m-0 font-anek font-semibold text-[24px] leading-[1.25] text-ink">
          আপনার নম্বর দিন
        </h1>
        <div className="font-anek font-normal text-[15px] leading-[1.75] text-body mt-[7px]">
          এই নম্বরেই একটি কোড পাঠানো হবে।
        </div>
      </div>

      <div className="mt-[28px]">
        <label htmlFor="a3-phone" className={FIELD_LABEL}>
          মোবাইল নম্বর
        </label>
        <div className="flex gap-[9px]">
          <div className="border border-border bg-white w-[82px] h-[58px] flex items-center justify-center font-anek font-medium text-[17px] leading-[1.75] text-body">
            +৮৮০
          </div>
          <input
            id="a3-phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            autoFocus
            value={door.number}
            placeholder={A3_PLACEHOLDER}
            aria-invalid={door.fieldError !== null ? true : undefined}
            aria-describedby={door.fieldError !== null ? 'a3-phone-error' : 'a3-phone-hint'}
            onChange={(e) => door.setNumber(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !door.disabled) door.submit();
            }}
            className={
              'flex-1 min-w-0 h-[58px] px-[15px] font-manrope font-medium text-[19px] leading-[1] text-ink ' +
              '[font-variant-numeric:tabular-nums] outline-none rounded-none placeholder:text-muted placeholder:font-normal ' +
              (door.fieldError !== null ? FIELD_ERROR_BOX : 'border-[1.5px] border-teal bg-white')
            }
          />
        </div>
        {/* The error wins: § 02 renders one line under a field, never both. */}
        {door.fieldError !== null ? (
          <div id="a3-phone-error" role="alert" className={FIELD_ERROR_LINE + ' mt-[8px]'}>
            {door.fieldError}
          </div>
        ) : (
          <div id="a3-phone-hint" className="font-anek font-normal text-[13px] leading-[1.75] text-muted mt-[8px]">
            সিমে যেভাবে লেখা, সেভাবেই লিখুন।
          </div>
        )}
      </div>

      {door.options !== null && door.options.length > 1 ? (
        <div className="mt-[22px]">
          <ChannelChoice options={door.options} chosen={door.chosen} onChoose={door.choose} />
        </div>
      ) : null}

      <TelegramPanel step={door.telegram} onReopen={door.reopenTelegram} className="mt-[14px]" />

      <div className="flex-1 min-h-[16px]" />

      {/*
        The Turnstile widget, rendered `interaction-only` and therefore drawing nothing
        at all unless Cloudflare wants the person to do something; `empty:hidden` keeps
        the container out of the layout while it is empty. See turnstile.ts.
      */}
      <div ref={door.challenge} className="empty:hidden flex" />

      <p className="m-0 font-anek font-normal text-[13px] leading-[1.75] text-body mb-[15px]">
        এগিয়ে গেলে আপনি অপ্রাপ্য-র{' '}
        <Link to="/policy/terms-of-service" className="text-link font-medium">
          শর্তাবলি
        </Link>{' '}
        ও{' '}
        <Link to="/policy/privacy-policy" className="text-link font-medium">
          গোপনীয়তা নীতি
        </Link>{' '}
        মেনে নিচ্ছেন।
      </p>

      <DoorNoticeLine notice={door.notice} className="mb-[10px]" />

      <Button tone="primary" size="lg" disabled={door.disabled} onClick={door.submit}>
        {door.primaryLabel}
      </Button>
    </Frame>
  );
}
